// Interface defining the structure of cached items
export interface CacheItem<T> {
  data: T;                    // The actual data being cached
  timestamp: number;          // When the item was cached
  expiry?: number;           // Optional expiry time in milliseconds
}

/**
 * Service class for handling browser localStorage caching operations
 */
class StorageService {
  /**
   * Stores data in localStorage with optional expiry
   * @param key - Unique identifier for cached item
   * @param data - Data to be cached
   * @param expiryMs - Optional expiry time in milliseconds
   */
  static setCache<T>(key: string, data: T, expiryMs?: number): void {
    try {
      const item: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        expiry: expiryMs
      };
      localStorage.setItem(key, JSON.stringify(item));
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  }

  /**
   * Retrieves data from localStorage if it exists and hasn't expired
   * @param key - Unique identifier for cached item
   * @returns The cached data or null if not found/expired
   */
  static getCache<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      const cached: CacheItem<T> = JSON.parse(item);
      
      // Remove and return null if item has expired
      if (cached.expiry && Date.now() - cached.timestamp > cached.expiry) {
        localStorage.removeItem(key);
        return null;
      }

      return cached.data;
    } catch (error) {
      console.error('Error reading from cache:', error);
      return null;
    }
  }

  /**
   * Clears either a specific cache entry or all cached data
   * @param key - Optional specific key to clear. If not provided, clears all cache
   */
  static clearCache(key?: string): void {
    if (key) {
      localStorage.removeItem(key);
    } else {
      localStorage.clear();
    }
  }
}

export default StorageService; 